import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import LayoutHeader from './components/LayoutHeader';
import DashboardSection from './components/DashboardSection';
import TicketManagementSection from './components/TicketManagementSection';
import TicketNewForm from './components/TicketNewForm';
import AuthLogin from './components/AuthLogin';
import TicketCaseDetail from './components/TicketCaseDetail';
import { defaultMetrics } from './mock/metrics';
import { defaultTickets } from './mock/tickets';

const App = () => {

  // Función de login que carga el dashboard con el rango de fechas actual
  const handleLogin = () => {
    setIsLoggedIn(true);
    setCurrentPage('Dashboard');
    fetchAndCalculateMetrics(fechaInicio, fechaFin);
  };


  const [currentPage, setCurrentPage] = useState('Login'); // 'Login', 'Dashboard', 'Tickets', 'NewTicket', 'TicketDetail'
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [selectedTicketForDetail, setSelectedTicketForDetail] = useState(null);
  const [tickets, setTickets] = useState([]); // Estado para gestionar los tickets
  const [metrics, setMetrics] = useState([]); // Métricas dinámicas para el dashboard
  const [fechaInicio, setFechaInicio] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01 00:00`;
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} 23:59`;
  });

  // Consulta casos por rango de fechas y calcula métricas
  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const fetchAndCalculateMetrics = (inicio, fin) => {
    fetch(`${API_URL}/webhook/listar_casos_por_fecha`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fecha_inicio: inicio, fecha_fin: fin })
    })
      .then(res => res.json())
      .then(json => {
        if (!json.error && Array.isArray(json.data)) {
          const allTickets = json.data;
          setTickets(allTickets);
          // Calcular métricas dinámicas
          const total = allTickets.length;
          const abiertos = allTickets.filter(t => t.status?.name === 'Abierto').length;
          const pendientes = allTickets.filter(t => t.status?.name === 'Pendiente').length;
          const cerrados = allTickets.filter(t => t.status?.name === 'Cerrado').length;
          const vencidos = allTickets.filter(t => {
            if (!t.timeEstimated) return false;
            const fecha = new Date(t.timeEstimated.replace(' ', 'T'));
            return fecha < new Date();
          }).length;
          // Métrica: casos por responsable (top 3)
          const responsablesCount = {};
          allTickets.forEach(t => {
            const resp = t.responsible?.name || 'Sin responsable';
            responsablesCount[resp] = (responsablesCount[resp] || 0) + 1;
          });
          const topResponsables = Object.entries(responsablesCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([resp, count]) => ({ nombre: `Casos de ${resp}`, valor: count, color: 'bg-purple-100 text-purple-700', icono: '👨‍💻' }));

          // Métrica: casos por sede (top 3)
          const sedesCount = {};
          allTickets.forEach(t => {
            const sede = t.form?.['Sede'] || 'Sin sede';
            sedesCount[sede] = (sedesCount[sede] || 0) + 1;
          });
          const topSedes = Object.entries(sedesCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([sede, count]) => ({ nombre: `Casos en ${sede}`, valor: count, color: 'bg-indigo-100 text-indigo-700', icono: '🏢' }));

          // Métrica: casos por campaña (top 3)
          const campaniasCount = {};
          allTickets.forEach(t => {
            const campania = t.form?.['Campaña'] || 'Sin campaña';
            campaniasCount[campania] = (campaniasCount[campania] || 0) + 1;
          });
          const topCampanias = Object.entries(campaniasCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([campania, count]) => ({ nombre: `Casos en ${campania}`, valor: count, color: 'bg-pink-100 text-pink-700', icono: '📢' }));

          // Métrica: casos por solicitante (top 3)
          const solicitantesCount = {};
          allTickets.forEach(t => {
            const sol = t.form?.['Nombre del Solicitante'] || 'Sin solicitante';
            solicitantesCount[sol] = (solicitantesCount[sol] || 0) + 1;
          });
          const topSolicitantes = Object.entries(solicitantesCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([sol, count]) => ({ nombre: `Solicitudes de ${sol}`, valor: count, color: 'bg-teal-100 text-teal-700', icono: '🙋‍♂️' }));

          setMetrics([
            { nombre: 'Total de casos', valor: total, color: 'bg-blue-100 text-blue-700', icono: '📊' },
            { nombre: 'Casos abiertos', valor: abiertos, color: 'bg-custom-green-100 text-custom-green-700', icono: '🟢' },
            { nombre: 'Casos pendientes', valor: pendientes, color: 'bg-yellow-100 text-yellow-700', icono: '🟡' },
            { nombre: 'Casos cerrados', valor: cerrados, color: 'bg-gray-200 text-gray-700', icono: '⚪' },
            { nombre: 'Casos vencidos', valor: vencidos, color: 'bg-red-100 text-red-700', icono: '🔴' },
            ...topResponsables,
            ...topSedes,
            ...topCampanias,
            ...topSolicitantes
          ]);
        }
      });
  };

  // Solo cargar datos en login y cuando el usuario cambie el filtro de fechas
  // El login ya llama a fetchAndCalculateMetrics
  // El filtro de fechas lo llama desde onFechaChange

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentPage('Login');
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
    setShowNewTicketModal(false); // Cerrar modal si se navega
    setSelectedTicketForDetail(null); // Limpiar detalle si se navega
  };

  const handleNewTicketSubmit = (newTicketData) => {
    const newTicket = {
      id: `TKT-${String(tickets.length + 1).padStart(3, '0')}`,
      subject: newTicketData.subject,
      requester: newTicketData.requester,
      email: newTicketData.email,
      phone: newTicketData.phone,
      category: newTicketData.category,
      description: newTicketData.description,
      assignedTo: 'Sin Asignar',
      status: 'Abierto',
      date: new Date().toISOString().slice(0, 10),
    };
    setTickets((prevTickets) => [...prevTickets, newTicket]);
    alert('Ticket creado con éxito!');
    setShowNewTicketModal(false);
    setCurrentPage('Tickets'); // Volver a la vista de tickets después de crear uno
  };

  const handleShowNewTicketForm = () => {
    setShowNewTicketModal(true);
  };

  const handleCloseNewTicketModal = () => {
    setShowNewTicketModal(false);
  };

  const handleViewTicketDetail = (ticket) => {
    setSelectedTicketForDetail(ticket);
    setCurrentPage('TicketDetail');
  };

  const handleBackFromDetail = () => {
    setSelectedTicketForDetail(null);
    setCurrentPage('Tickets');
  };

  // Extraer lista única de responsables de los tickets
  const responsablesUnicos = Array.from(new Set(
    tickets.map(t => t.responsible?.name || t.responsable || '').filter(Boolean)
  ));

  return (
    <BrowserRouter>
      <Routes>
        {/* Ruta pública para el formulario de ticket sin login ni header */}
        <Route path="/nuevo-ticket/*" element={<TicketNewForm />} />
        {/* Resto de la app protegida por login */}
        <Route
          path="/*"
          element={
            !isLoggedIn ? (
              <AuthLogin onLogin={handleLogin} />
            ) : (
              <div className="min-h-screen bg-gray-100 font-sans antialiased">
                <style>{`
                  .bg-custom-blue {
                    background-color: #007bff;
                  }
                  .text-custom-blue {
                    color: #007bff;
                  }
                  .focus\:ring-custom-blue:focus {
                    --tw-ring-color: #007bff;
                  }
                `}</style>
                <LayoutHeader currentPage={currentPage} onNavigate={handleNavigate} onLogout={handleLogout} />
                <main>
                  {currentPage === 'Dashboard' && (
                    <DashboardSection
                      metrics={metrics}
                      fechaInicio={fechaInicio}
                      fechaFin={fechaFin}
                      onFechaChange={(inicio, fin) => {
                        setFechaInicio(inicio);
                        setFechaFin(fin);
                        fetchAndCalculateMetrics(inicio, fin);
                      }}
                    />
                  )}
                  {currentPage === 'Tickets' && (
                    <TicketManagementSection tickets={tickets} onNewTicket={handleShowNewTicketForm} onSelectTicket={handleViewTicketDetail} />
                  )}
                  {currentPage === 'NewTicket' && (
                    <TicketNewForm onSubmit={handleNewTicketSubmit} />
                  )}
                  {currentPage === 'TicketDetail' && (
                    <TicketCaseDetail ticket={selectedTicketForDetail} onBack={handleBackFromDetail} responsables={responsablesUnicos} />
                  )}
                </main>
                {showNewTicketModal && (
                  <TicketNewForm onSubmit={handleNewTicketSubmit} onClose={handleCloseNewTicketModal} isModal={true} />
                )}
              </div>
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
